import type { AuthResponse } from '../types';
import {
  announceScopeChange,
  supportSession,
  SUPPORT_ENDED_EVENT,
  type RecoveryOutcome,
  type SupportEndReason,
  type SupportEndedDetail,
} from '../auth/supportSession';

// Single source of truth for the API origin. The `|| localhost` fallback is an
// app-wide convention for local dev; real staging/prod builds always inject
// VITE_API_URL (Amplify branch env). Exported so SSO (sso.ts) builds its
// full-page authorize URL off the same base instead of duplicating the fallback.
export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';

export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

export const tokens = {
  getAccess: (): string | null => localStorage.getItem(ACCESS_KEY),
  getRefresh: (): string | null => localStorage.getItem(REFRESH_KEY),
  setTokens: (access: string, refresh: string): void => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refresh = tokens.getRefresh();
    if (!refresh) {
      tokens.clearTokens();
      window.dispatchEvent(new Event('auth:lost'));
      throw new ApiError(401, 'No refresh token');
    }
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) {
      const detail = await extractDetail(res);
      // Inside a client org under a support grant (SC-2948), a 401 refresh
      // means the grant expired or was revoked — not that staff are signed out.
      // (Only a 401: a 5xx says nothing about the grant.)
      // Fall back to the platform session instead of dropping the user to
      // /login. Still throw: the request that tripped this was scoped to the
      // client org, so retrying it in the platform scope would only fail again.
      if (res.status === 401) {
        const outcome = await recoverPlatformSession('lost');
        if (outcome === 'recovered') throw new ApiError(401, 'Support access ended');
        // Offline / server trouble, or the session changed while we waited:
        // fail this request but leave the session (and any newer one) intact.
        if (outcome === 'retry' || outcome === 'superseded') throw new ApiError(401, detail);
      }
      tokens.clearTokens();
      window.dispatchEvent(new Event('auth:lost'));
      throw new ApiError(res.status, detail);
    }
    const data = (await res.json()) as AuthResponse;
    tokens.setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Leave a support session WITHOUT the support scope's help (SC-2948): refresh
 * with the platform refresh token stashed on entry, swap the token pair, and
 * broadcast `support:ended` so AuthProvider re-hydrates and routes. Used when the
 * grant has died under us (the support-scoped tokens no longer work, so the
 * audited `/auth/switch-org` exit isn't reachable either).
 *
 * Returns false — leaving tokens untouched — when there's no support session to
 * recover from, or the platform token is dead too (the caller then treats it as
 * a real sign-out). The stash is cleared either way: it's single-use.
 */
let recoveryPromise: Promise<RecoveryOutcome> | null = null;

export async function recoverPlatformSession(reason: SupportEndReason): Promise<RecoveryOutcome> {
  // The expiry timer and a 401-triggered refresh can both reach here at once.
  // Share one attempt: the loser would otherwise find the stash already
  // cleared, report "nothing to recover", and sign staff out mid-recovery.
  if (recoveryPromise) return recoveryPromise;
  recoveryPromise = recover(reason);
  try {
    return await recoveryPromise;
  } finally {
    recoveryPromise = null;
  }
}

async function recover(reason: SupportEndReason): Promise<RecoveryOutcome> {
  const stash = supportSession.read();
  if (!stash) return 'unrecoverable';
  // The session this recovery belongs to. Anything that replaces it while the
  // request is out (sign-out, a new login, an Exit) must win.
  const deadSupportRefresh = tokens.getRefresh();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: stash.platformRefreshToken }),
    });
  } catch (e) {
    // Offline: keep the stash — it's still the way back once we reconnect.
    if (e instanceof TypeError) return 'retry';
    throw e;
  }
  // Only an auth rejection proves the staff session is gone; a 5xx doesn't.
  if (res.status === 401 || res.status === 403) {
    supportSession.clear();
    return 'unrecoverable';
  }
  if (!res.ok) return 'retry';
  const data = (await res.json()) as AuthResponse;
  if (tokens.getRefresh() !== deadSupportRefresh) {
    // Superseded mid-flight. Don't resurrect a signed-out session or overwrite
    // a newer one — and revoke what we just minted, since nothing will use it.
    void fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: data.refresh_token }),
    }).catch(() => {});
    return 'superseded';
  }
  supportSession.clear();
  tokens.setTokens(data.access_token, data.refresh_token);
  announceScopeChange();
  // Revoke the client-org refresh token we're abandoning. It's useless now, but
  // a re-grant revives the same membership row — and with it this token.
  if (deadSupportRefresh && deadSupportRefresh !== data.refresh_token) {
    void fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: deadSupportRefresh }),
    }).catch(() => {});
  }
  const detail: SupportEndedDetail = { organizationName: stash.organizationName, reason };
  window.dispatchEvent(new CustomEvent(SUPPORT_ENDED_EVENT, { detail }));
  return 'recovered';
}

async function extractDetail(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (body && typeof body.detail === 'string') return body.detail;
    // FastAPI's default 422 shape is { detail: [{ loc, msg, type }, ...] }.
    // Surface the first error in human-readable form: drop technical detail
    // after the first colon, substitute Pydantic's leading "value" with the
    // actual field name, and capitalize. Real fix lives in SC-1606 (backend
    // normalizing 422 messages); this is the frontend's defensive polish.
    if (Array.isArray(body?.detail) && body.detail.length > 0) {
      const first = body.detail[0];
      if (typeof first?.msg === 'string') {
        // loc can include integer array indices (e.g. ["body", 0, "email"]) —
        // only use the last element as a field name if it's a string, otherwise
        // skip the prefix to avoid producing things like "0 is not a valid...".
        const lastLoc = Array.isArray(first.loc) && first.loc.length > 0
          ? first.loc[first.loc.length - 1]
          : undefined;
        const field = typeof lastLoc === 'string' ? lastLoc.replace(/_/g, ' ') : null;
        let msg = first.msg.split(':')[0].trim();
        if (field) msg = msg.replace(/^value\b/i, field);
        return msg.charAt(0).toUpperCase() + msg.slice(1);
      }
    }
  } catch {
    // not JSON
  }
  return res.statusText || `HTTP ${res.status}`;
}

interface RequestOptions {
  method?: string;
  body?: BodyInit | null;
  headers?: Record<string, string>;
}

export interface ResponseWithStatus<T> {
  data: T;
  status: number;
}

// Core request path. Returns the parsed body alongside the HTTP status so
// callers that need to discriminate 2xx codes (e.g. 200-reuse vs 201-created)
// can do so. `request<T>` is the thin status-discarding wrapper most callers
// use; reach for `requestWithMeta` only when the status code is load-bearing.
async function requestWithMeta<T>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<ResponseWithStatus<T>> {
  const { method = 'GET', body, headers = {} } = options;
  const finalHeaders: Record<string, string> = { ...headers };

  const access = tokens.getAccess();
  if (access) finalHeaders['Authorization'] = `Bearer ${access}`;

  const res = await fetch(`${API_BASE}${path}`, { method, headers: finalHeaders, body });

  if (res.status === 401 && !isRetry) {
    try {
      await refreshAccessToken();
    } catch {
      throw new ApiError(401, await extractDetail(res));
    }
    return requestWithMeta<T>(path, options, true);
  }

  if (!res.ok) {
    throw new ApiError(res.status, await extractDetail(res));
  }

  if (res.status === 204) return { data: undefined as T, status: res.status };
  return { data: (await res.json()) as T, status: res.status };
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { data } = await requestWithMeta<T>(path, options);
  return data;
}

function jsonOpts(body: unknown): RequestOptions {
  return {
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    body !== undefined
      ? request<T>(path, { method: 'POST', ...jsonOpts(body) })
      : request<T>(path, { method: 'POST' }),
  // Like `post`, but resolves to `{ data, status }` so the caller can branch
  // on the 2xx code. Used by createChatSession to distinguish 201 (new
  // session → refresh briefing) from 200 (idempotent reuse → skip the
  // expensive narrative regen).
  postWithStatus: <T>(path: string, body?: unknown) =>
    body !== undefined
      ? requestWithMeta<T>(path, { method: 'POST', ...jsonOpts(body) })
      : requestWithMeta<T>(path, { method: 'POST' }),
  patch: <T>(path: string, body?: unknown) =>
    body !== undefined
      ? request<T>(path, { method: 'PATCH', ...jsonOpts(body) })
      : request<T>(path, { method: 'PATCH' }),
  // A replace-on-write body (the request IS the whole resource), e.g. the
  // catalog's pinned filter groups (SC-3466).
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', ...jsonOpts(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),
};

interface StreamRequestOptions {
  method?: string;
  body?: BodyInit | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

// Returns the raw Response so the caller can consume `body` as a stream.
// Shares the auth / 401-refresh dance with request<T>.
export async function requestStream(
  path: string,
  options: StreamRequestOptions = {},
  isRetry = false,
): Promise<Response> {
  const { method = 'GET', body, headers = {}, signal } = options;
  const finalHeaders: Record<string, string> = { ...headers };

  const access = tokens.getAccess();
  if (access) finalHeaders['Authorization'] = `Bearer ${access}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: finalHeaders,
    body,
    signal,
  });

  if (res.status === 401 && !isRetry) {
    try {
      await refreshAccessToken();
    } catch {
      throw new ApiError(401, await extractDetail(res));
    }
    return requestStream(path, options, true);
  }

  if (!res.ok) {
    throw new ApiError(res.status, await extractDetail(res));
  }
  return res;
}

export interface DownloadedFile {
  blob: Blob;
  // The raw response headers — the caller owns which of them it trusts (a
  // `Content-Disposition` filename is server-supplied and must be sanitised
  // before it names a file; see `fileDownload.ts`).
  headers: Headers;
}

// POST a JSON body and read the response as file bytes (SC-3149). Rides
// `requestStream` so the Bearer header and the 401-refresh retry apply — the
// FE has no cookie auth, so a plain `<a href download>` would 401.
export async function downloadFile(path: string, body: unknown): Promise<DownloadedFile> {
  const res = await requestStream(path, { method: 'POST', ...jsonOpts(body) });
  return { blob: await res.blob(), headers: res.headers };
}
